import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  PlusIcon, WalletIcon, ImageSquareIcon, PencilSimpleIcon, TrashIcon,
  ArrowClockwiseIcon, ReceiptIcon, CheckCircleIcon, XCircleIcon, HourglassIcon,
} from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const currentMonth = () => new Date().toISOString().slice(0, 7);
const todayISO = () => new Date().toISOString().slice(0, 10);

const CATEGORIES = [
  { key: "utilities", label: "Utilities" },
  { key: "supplies", label: "Supplies" },
  { key: "maintenance", label: "Maintenance" },
  { key: "salaries", label: "Salaries" },
  { key: "marketing", label: "Marketing" },
  { key: "food_bev", label: "Food & Beverage" },
  { key: "taxes", label: "Taxes" },
  { key: "rent", label: "Rent" },
  { key: "travel", label: "Travel" },
  { key: "other", label: "Other" },
];

const CATEGORY_TONE = {
  utilities: "bg-[hsl(45_82%_88%)] text-[hsl(38_62%_35%)]",
  supplies: "bg-secondary/15 text-secondary",
  maintenance: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  salaries: "bg-primary/15 text-primary",
  marketing: "bg-[hsl(0_60%_92%)] text-[hsl(0_60%_35%)]",
  food_bev: "bg-[hsl(140_50%_92%)] text-[hsl(140_40%_30%)]",
  taxes: "bg-muted text-muted-foreground",
  rent: "bg-[hsl(210_50%_92%)] text-[hsl(210_50%_35%)]",
  travel: "bg-[hsl(280_50%_92%)] text-[hsl(280_50%_35%)]",
  other: "bg-muted text-muted-foreground",
};

const EMPTY = {
  category: "utilities",
  amount: "",
  vendor: "",
  date: todayISO(),
  payment_mode: "cash",
  gst_claimable: false,
  gst_amount: "",
  receipt_image: "",
  notes: "",
  is_recurring: false,
  recurring_frequency: "monthly",
  property_id: "",
};

function ExpenseForm({ initial, properties, onSubmit, submitLabel, testIdSubmit }) {
  const [form, setForm] = useState(initial);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, receipt_image: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      amount: Number(form.amount) || 0,
      gst_amount: form.gst_claimable ? Number(form.gst_amount) || 0 : 0,
      property_id: form.property_id || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Amount (₹)</Label>
          <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="expense-amount" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="expense-vendor" /></div>
        <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="expense-date" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Payment mode</Label>
          <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
            <SelectTrigger data-testid="expense-payment-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="bank_transfer">Bank transfer</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Property (optional)</Label>
          <Select value={form.property_id || "__all__"} onValueChange={(v) => setForm({ ...form, property_id: v === "__all__" ? "" : v })}>
            <SelectTrigger data-testid="expense-property"><SelectValue placeholder="Group-wide" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Group-wide</SelectItem>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-3 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.gst_claimable} onCheckedChange={(v) => setForm({ ...form, gst_claimable: !!v })} data-testid="expense-gst-toggle" />
          <span className="text-sm font-medium">GST / ITC claimable</span>
        </label>
        {form.gst_claimable && (
          <div>
            <Label className="text-xs">GST amount (₹) — flows into your input tax credit</Label>
            <Input type="number" step="0.01" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} data-testid="expense-gst-amount" />
          </div>
        )}
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-3 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.is_recurring} onCheckedChange={(v) => setForm({ ...form, is_recurring: !!v })} data-testid="expense-recurring-toggle" />
          <span className="text-sm font-medium">Recurring expense</span>
        </label>
        {form.is_recurring && (
          <div>
            <Label className="text-xs">Frequency</Label>
            <Select value={form.recurring_frequency} onValueChange={(v) => setForm({ ...form, recurring_frequency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div>
        <Label>Receipt photo (optional)</Label>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="expense-receipt-input" />
        <div className="flex items-center gap-3 mt-1">
          <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="expense-receipt-btn">
            <ImageSquareIcon size={14} className="mr-1" /> {form.receipt_image ? "Replace image" : "Upload"}
          </Button>
          {form.receipt_image && (
            <>
              <img src={form.receipt_image} alt="receipt" className="h-14 w-14 rounded-md object-cover border border-border" />
              <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, receipt_image: "" })}>Remove</Button>
            </>
          )}
        </div>
      </div>

      <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional context…" /></div>

      <DialogFooter><Button type="submit" data-testid={testIdSubmit}>{submitLabel}</Button></DialogFooter>
    </form>
  );
}

export default function Expenses() {
  const { properties } = useProperty();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, gst_input_claimable: 0, count: 0, by_category: {} });
  const [month, setMonth] = useState(currentMonth());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [propFilter, setPropFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [preview, setPreview] = useState(null);

  const canApprove = user?.role === "admin";

  const load = async () => {
    const params = { month };
    if (propFilter) params.property_id = propFilter;
    if (catFilter) params.category = catFilter;
    if (statusFilter) params.status = statusFilter;
    const [lRes, sRes] = await Promise.all([
      api.get("/expenses", { params }),
      api.get("/expenses/summary", { params: { month, property_id: propFilter || undefined }, silent: true }).catch(() => ({ data: null })),
    ]);
    setItems(lRes.data);
    setSummary(sRes.data || { total: 0, gst_input_claimable: 0, count: 0, by_category: {} });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, propFilter, catFilter, statusFilter]);

  const approve = async (e) => {
    try { await api.post(`/expenses/${e.id}/approve`); toast.success("Approved"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const reject = async (e) => {
    const reason = window.prompt("Rejection reason (optional)?") ?? "";
    try { await api.post(`/expenses/${e.id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const create = async (form) => {
    try {
      await api.post("/expenses", form);
      toast.success("Expense added");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async (form) => {
    try {
      await api.patch(`/expenses/${editing.id}`, form);
      toast.success("Expense updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (e) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const topCategory = useMemo(() => {
    const entries = Object.entries(summary.by_category || {});
    if (entries.length === 0) return null;
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0];
  }, [summary]);

  return (
    <div>
      <PageHeader
        overline="Books"
        title="Expenses"
        description="Track every rupee that leaves the group — utilities, supplies, salaries, rent and more. GST-claimable expenses feed into your input tax credit."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="expense-new-btn"><PlusIcon size={16} className="mr-1" /> New expense</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New expense</DialogTitle></DialogHeader>
              <ExpenseForm initial={EMPTY} properties={properties} onSubmit={create} submitLabel="Add expense" testIdSubmit="expense-submit-btn" />
            </DialogContent>
          </Dialog>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5">
          <p className="label-overline">Total {month}</p>
          <p className="font-display text-3xl mt-2">{inr(summary.total)}</p>
          <p className="text-xs text-muted-foreground mt-1">{summary.count} entries</p>
        </Card>
        <Card className="p-5">
          <p className="label-overline">Input GST claimable</p>
          <p className="font-display text-3xl mt-2 text-secondary">{inr(summary.gst_input_claimable)}</p>
          <p className="text-xs text-muted-foreground mt-1">Adjust against output GST</p>
        </Card>
        <Card className="p-5">
          <p className="label-overline">Top category</p>
          <p className="font-display text-2xl mt-2">{topCategory ? CATEGORIES.find((c) => c.key === topCategory[0])?.label || topCategory[0] : "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">{topCategory ? inr(topCategory[1]) : "No spend yet"}</p>
        </Card>
        <Card className="p-5">
          <p className="label-overline">Recurring</p>
          <p className="font-display text-3xl mt-2 flex items-center gap-2">
            {items.filter((x) => x.is_recurring).length}
            <ArrowClockwiseIcon size={20} className="text-muted-foreground" />
          </p>
          <p className="text-xs text-muted-foreground mt-1">Auto-repeats each period</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-40" data-testid="expense-month-filter" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Property</Label>
            <Select value={propFilter || "__all__"} onValueChange={(v) => setPropFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="mt-1 w-56" data-testid="expense-property-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All properties</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
            <Select value={catFilter || "__all__"} onValueChange={(v) => setCatFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="mt-1 w-48" data-testid="expense-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
            <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="mt-1 w-48" data-testid="expense-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                <SelectItem value="pending_approval">Pending approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>GST</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`} className={e.status === "pending_approval" ? "bg-[hsl(var(--warning))]/5" : e.status === "rejected" ? "opacity-60" : ""}>
                <TableCell className="text-xs font-mono-plex">{e.date}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={CATEGORY_TONE[e.category] || CATEGORY_TONE.other}>
                    {CATEGORIES.find((c) => c.key === e.category)?.label || e.category}
                  </Badge>
                  {e.is_recurring && <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><ArrowClockwiseIcon size={10} />{e.recurring_frequency}</span>}
                  {e.auto_generated && <span className="ml-2 text-[9px] uppercase tracking-wide text-primary bg-primary/10 border border-primary/30 rounded px-1.5 py-0.5" data-testid={`expense-auto-${e.id}`}>Auto · Payroll</span>}
                </TableCell>
                <TableCell className="text-sm">{e.vendor || "—"}</TableCell>
                <TableCell className="font-mono-plex">{inr(e.amount)}</TableCell>
                <TableCell className="text-xs uppercase tracking-wide text-muted-foreground">{e.payment_mode?.replace("_", " ")}</TableCell>
                <TableCell>
                  {e.gst_claimable ? (
                    <span className="text-xs text-secondary font-medium">ITC {inr(e.gst_amount)}</span>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  {e.status === "pending_approval" && (
                    <Badge variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><HourglassIcon size={10} className="mr-1" />Pending</Badge>
                  )}
                  {e.status === "rejected" && (
                    <Badge variant="outline" className="bg-destructive/15 text-destructive" title={e.rejection_reason || ""}><XCircleIcon size={10} className="mr-1" />Rejected</Badge>
                  )}
                  {(!e.status || e.status === "approved") && (
                    <Badge variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"><CheckCircleIcon size={10} className="mr-1" />Approved</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {e.receipt_image ? (
                    <button onClick={() => setPreview(e.receipt_image)} className="hover:opacity-80" data-testid={`expense-receipt-view-${e.id}`}>
                      <img src={e.receipt_image} alt="" className="h-9 w-9 rounded object-cover border border-border" />
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canApprove && e.status === "pending_approval" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => approve(e)} className="text-[hsl(var(--success))] hover:text-[hsl(var(--success))]" data-testid={`expense-approve-${e.id}`}>
                          <CheckCircleIcon size={12} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(e)} className="text-destructive hover:text-destructive" data-testid={`expense-reject-${e.id}`}>
                          <XCircleIcon size={12} />
                        </Button>
                      </>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => setEditing(e)} data-testid={`expense-edit-${e.id}`}><PencilSimpleIcon size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(e)} className="text-destructive hover:text-destructive" data-testid={`expense-delete-${e.id}`}><TrashIcon size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                <WalletIcon size={28} className="mx-auto mb-2 opacity-40" />
                No expenses recorded for {month}.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit expense</DialogTitle></DialogHeader>
            <ExpenseForm
              initial={{
                ...editing,
                amount: editing.amount,
                gst_amount: editing.gst_amount || "",
                property_id: editing.property_id || "",
              }}
              properties={properties}
              onSubmit={saveEdit}
              submitLabel="Save changes"
              testIdSubmit="expense-edit-save-btn"
            />
          </DialogContent>
        </Dialog>
      )}

      {preview && (
        <Dialog open onOpenChange={(v) => !v && setPreview(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Receipt</DialogTitle></DialogHeader>
            <img src={preview} alt="receipt" className="w-full h-auto rounded-md border border-border" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
